import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    Self,
    TemplateRef,
} from '@angular/core';
import { FormControl, NgControl } from '@angular/forms';
import { filter, map, tap } from 'rxjs/operators';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Observable, shareReplay } from 'rxjs';
import { latinizeStr } from '@bazis/utils';
import { SearchSettings } from '@bazis/shared/models/srv.types';
import { BazisConfigurationService, SHARE_REPLAY_SETTINGS } from '@bazis/configuration.service';
import { BazisEntityService } from '@bazis/shared/services/entity.service';
import { BazisControlValueAccessor } from '@bazis/form/components/control-value-accessor.component';

@UntilDestroy()
@Component({
    selector: 'bazis-input-hint',
    templateUrl: './input-hint.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class BazisInputHintComponent extends BazisControlValueAccessor {
    @Input() entityType: string = null;

    @Input() searchSettings: SearchSettings = null;

    @Input() infiniteListTpl: TemplateRef<any>;

    @Input() multiple: boolean = false;

    @Input() nameField: string = null; // use value from item.$snapshot[nameField] to display item in the list

    @Input() mapSettings: boolean = false;

    @Input() mapLayers: boolean = false;

    @Input() excludeIds$: Observable<string[]> = null;

    @Input() forceLatin: boolean = false; // force latin symbols in input field

    @Input() emptyListKey = '';

    searchControl: FormControl<string | null> = new FormControl();

    emptyValueEntity = null;

    protected _isInited = false;

    isDisabled = false;

    showList: boolean = false;

    searchValue$ = this.searchControl.valueChanges.pipe(
        map((value) => {
            if (this.forceLatin && value) {
                let newValue = latinizeStr(value.toUpperCase());
                if (value !== newValue) {
                    this.searchControl.setValue(newValue);
                    return undefined;
                }
            }
            return value;
        }),
        filter((v) => v !== undefined),
        tap((value) => {
            this.onChange(value);
        }),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    constructor(
        @Self() public ngControl: NgControl,
        protected cdr: ChangeDetectorRef,
        public configurationService: BazisConfigurationService,
        protected entityService: BazisEntityService,
    ) {
        super(ngControl, cdr, configurationService);
        ngControl.valueAccessor = this;
    }

    extendOnInit() {
        if (!this.entityType && this.searchSettings?.entityType) {
            this.entityType = this.searchSettings.entityType;
        }

        this._isInited = true;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
        if (isDisabled) this.searchControl.disable();
        else this.searchControl.enable();
    }

    public writeValue(value): void {
        if (!this._isInited) return;
        this.searchControl.setValue(value);
    }

    selectValue(value: string = '') {
        this.searchControl.setValue(value, { emitEvent: false });
        this.onChange(value);
        this.hideList();
    }

    toggleList() {
        if (this.showList && this.isFocused) return;
        this.showList = !this.showList;
        this.markAsTouched();
    }

    hideList() {
        if (!this.isFocused) this.showList = false;
    }
}
